/**
 * ConversationsFooterPanel — the Conversations footer popover (UI-SPEC §2.24, C15): the
 * equation from `GET .../conversations/load`, the note, and the brand-global conversation
 * cadence squares.
 *
 * When it opens every square but the selected one is previewed (lot A's
 * `POST .../allowances/preview { kind: 'conversationRuns', scope: 'footer', change: { cadence } }`).
 * [B] a `refused` square is disabled with its §6.2 suffix; a click on a square not yet answered
 * previews, then writes only if it fits. [A] a square whose preview carries warnings is amber and
 * opens the C17.3 dialog, never disabled; « Manuel » is never amber. A click writes ONCE
 * (`PUT .../conversations/cadence`), then the panel takes the response's `load`.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AllowanceFigures, Cadence, ConversationLoad, SaveWarnings } from '@traaker/shared';
import { cadenceToDays } from '@traaker/shared';
import { QuotaWarningDialog } from '../QuotaWarningDialog';
import { cadenceKey, cadencePips, isUnbounded, saveErrorOf, selectedPip } from './conversationModel';
import { conversationsApi, isAgencyCheck } from './conversationsApi';
import type { SaveCheck } from './conversationsApi';

export interface ConversationsFooterPanelProps {
  brandId: string;
  isAgency: boolean;
  load: ConversationLoad;
  /** A cadence write answered: the popup takes its `load` (footer, form figures). */
  onLoad: (load: ConversationLoad) => void;
}

type PipState = { loading: boolean; check?: SaveCheck; refusedBy?: AllowanceFigures };

/** The footer bar's numbers (C15): agency credits per month, a non-agency -1 « non configuré », else used / allowance runs. */
export function conversationBarNums(load: ConversationLoad | null, isAgency: boolean, t: (k: string, o?: Record<string, unknown>) => string): string | undefined {
  if (!load) return undefined;
  // Agency « Manuel » schedules nothing: « 0 crédits par mois » read as a broken figure.
  if (isAgency) return load.cadence === 'off' ? t('promptStudio.pop.manual') : t('promptStudio.pop.creditsResult', { n: load.brandCreditsPerMonth ?? 0 });
  if (isUnbounded(load.figures, false)) return t('promptStudio.foot.convUnbounded');
  return t('promptStudio.foot.convRuns', { used: load.figures.used, cap: load.figures.allowance });
}

function EqBox({ children, amber, primary }: { children: ReactNode; amber?: boolean; primary?: boolean }) {
  return (
    <Box sx={{
      border: '1px solid', borderColor: amber ? '#f59e0b' : primary ? '#7c3aed' : 'divider', borderRadius: '10px', px: 1.5, py: 1, minHeight: 42,
      display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
      bgcolor: amber ? 'rgba(245,158,11,.1)' : primary ? 'rgba(124,58,237,.06)' : 'background.paper',
      color: amber ? '#92400e' : primary ? '#5b21b6' : 'text.primary',
    }}>
      {children}
    </Box>
  );
}
const Op = ({ children }: { children: ReactNode }) => <Box sx={{ fontSize: 15, fontWeight: 600, color: 'text.disabled' }}>{children}</Box>;
const Unit = ({ children }: { children: ReactNode }) => <Box component="span" sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.secondary' }}>{children}</Box>;

export function ConversationsFooterPanel({ brandId, isAgency, load, onLoad }: ConversationsFooterPanelProps) {
  const { t } = useTranslation();
  const pips = cadencePips(isAgency);
  const selected = selectedPip(load.cadence, pips);
  const [states, setStates] = useState<Record<string, PipState>>({});
  const [saving, setSaving] = useState(false);
  // A double click writes once (U5): the state above is stale within one tick, this is not.
  const busy = useRef(false);
  const [line, setLine] = useState<string | null>(null);
  const [warn, setWarn] = useState<{ warnings: SaveWarnings[]; cadence: Cadence } | null>(null);
  const f = load.figures;
  const unbounded = isUnbounded(f, isAgency);

  // Every square but the selected one, again after each write (the figures moved).
  useEffect(() => {
    let live = true;
    setStates({});
    for (const pip of pips) {
      const key = cadenceKey(pip);
      if (key === selected || pip === 'off') continue;
      setStates((s) => ({ ...s, [key]: { loading: true } }));
      conversationsApi.previewCadence(brandId, pip).then(
        (check) => { if (live) setStates((s) => ({ ...s, [key]: { loading: false, check } })); },
        () => { if (live) setStates((s) => ({ ...s, [key]: { loading: false } })); },
      );
    }
    return () => { live = false; };
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const refusedOf = (key: string): AllowanceFigures | null => {
    const s = states[key];
    if (s?.refusedBy) return s.refusedBy;
    return s?.check && !isAgencyCheck(s.check) && s.check.refused ? s.check.figuresAfter : null;
  };
  const warningsOf = (key: string): SaveWarnings[] => {
    const c = states[key]?.check;
    return c && isAgencyCheck(c) ? c.warnings : [];
  };

  const write = async (cadence: Cadence) => {
    setSaving(true);
    setLine(null);
    try {
      const r = await conversationsApi.setCadence(brandId, cadence);
      onLoad(r.load);
    } catch (err) {
      const e = saveErrorOf(err);
      if (e.kind === 'refused') setStates((s) => ({ ...s, [cadenceKey(cadence)]: { loading: false, refusedBy: e.figuresAfter } }));
      else setLine(t('promptStudio.common.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const click = async (pip: Cadence) => {
    const key = cadenceKey(pip);
    if (busy.current || key === selected || refusedOf(key)) return;
    busy.current = true;
    try {
      await clickOnce(pip, key);
    } finally {
      busy.current = false;
    }
  };
  const clickOnce = async (pip: Cadence, key: string) => {
    let check = states[key]?.check;
    if (!check && pip !== 'off') {
      // Not yet answered: preview on click, write only if it fits.
      setStates((s) => ({ ...s, [key]: { loading: true } }));
      try {
        check = await conversationsApi.previewCadence(brandId, pip);
        setStates((s) => ({ ...s, [key]: { loading: false, check } }));
      } catch {
        setStates((s) => ({ ...s, [key]: { loading: false } }));
        setLine(t('promptStudio.fit.previewFailed'));
        return;
      }
    }
    if (check && !isAgencyCheck(check) && check.refused) return;
    if (check && isAgencyCheck(check) && check.warnings.length > 0) {
      setWarn({ warnings: check.warnings, cadence: pip });
      return;
    }
    await write(pip);
  };

  const label = (pip: Cadence) => (pip === 'off' ? t('promptStudio.pop.manual') : pip.unit === 'months' ? '30' : String(cadenceToDays(pip)));
  const title = (pip: Cadence, key: string): string => {
    if (pip === 'off') return t('promptStudio.pop.manualTitle');
    const refused = refusedOf(key);
    const c = states[key]?.check;
    const monthly = pip.unit === 'months';
    const d = monthly ? 30 : cadenceToDays(pip);
    if (!isAgency) {
      const n = c && !isAgencyCheck(c) ? c.figuresAfter.sustainableNeed : undefined;
      const base = n === undefined
        ? t(monthly ? 'promptStudio.pop.cadPlainMonthly' : 'promptStudio.pop.cadPlain', { d })
        : t('promptStudio.pop.cadTitleConv', { d, n });
      if (!refused) return base;
      return base + t(refused.sustainableNeed > refused.allowance ? 'promptStudio.pop.overBudget' : 'promptStudio.pop.overMonth');
    }
    const w = warningsOf(key);
    if (w.length === 0) return t(monthly ? 'promptStudio.pop.cadPlainMonthly' : 'promptStudio.pop.cadPlain', { d });
    const n = w[0].creditsPerMonthAfter;
    return t(monthly ? 'promptStudio.pop.cadTitleMonthly' : 'promptStudio.pop.cadTitleCredits', { d, n }) + t('promptStudio.pop.overBalance');
  };

  // The equation: launchesInPeriod absent (legacy per-persona cadences) → the result box alone.
  const L = f.launchesInPeriod;
  const credits = load.brandCreditsPerMonth ?? 0;
  const resultAmber = !isAgency && !unbounded && !f.ok;
  const result = isAgency ? (
    <EqBox primary>{credits} <Unit>{t('promptStudio.pop.creditsPerMonth')}</Unit></EqBox>
  ) : (
    <EqBox amber={resultAmber} primary={!resultAmber}>
      {unbounded ? <>{f.sustainableNeed} <Unit>{t('promptStudio.pop.runsPerMonth')}</Unit></> : t('promptStudio.pop.convRunsResult', { n: f.sustainableNeed, cap: f.allowance })}
    </EqBox>
  );
  const perLaunch = isAgency ? (L && L > 0 ? Math.round(credits / L) : undefined) : f.perLaunch;

  return (
    <Box sx={{ maxWidth: 'min(620px, calc(100vw - 24px))', maxHeight: '62vh', overflowY: 'auto', p: '14px 16px 15px', bgcolor: 'background.paper' }}>
      <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>{t('promptStudio.pop.conversations')}</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
        {L !== undefined && perLaunch !== undefined && (
          <>
            <EqBox>{perLaunch} <Unit>{t(isAgency ? 'promptStudio.pop.creditsPerLaunch' : 'promptStudio.pop.runsPerLaunch')}</Unit></EqBox>
            <Op>×</Op>
            <EqBox>↻ {L}</EqBox>
            <Op>=</Op>
          </>
        )}
        {result}
      </Box>
      {unbounded && <Box sx={{ fontSize: 11.5, color: '#b45309', mb: 1 }}>{t('promptStudio.conv.unbounded')}</Box>}
      {resultAmber && <Box sx={{ fontSize: 11.5, color: '#b45309', mb: 1 }}>{t('promptStudio.pop.configOverConv')}</Box>}
      <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 1.25 }}>{t(isAgency ? 'promptStudio.pop.convNoteAgency' : 'promptStudio.pop.convNoteSelf')}</Box>

      <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mb: 0.75 }}>↻ {t('promptStudio.pop.daysConv')}</Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {pips.map((pip) => {
          const key = cadenceKey(pip);
          const isSelected = key === selected;
          const disabled = !isAgency && !!refusedOf(key);
          const amber = isAgency && warningsOf(key).length > 0;
          const loading = states[key]?.loading === true;
          return (
            <Tooltip key={key} title={title(pip, key)} arrow>
              <span>
                <Box component="button" disabled={disabled}
                  onClick={() => { if (!disabled) void click(pip); }}
                  sx={{
                    minWidth: 34, height: 32, px: pip === 'off' ? 1 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid', borderRadius: 1, fontSize: 12, fontWeight: 600, py: 0,
                    borderColor: isSelected ? (amber ? '#f59e0b' : '#7c3aed') : amber ? 'rgba(245,158,11,.45)' : 'divider',
                    bgcolor: isSelected ? (amber ? '#f59e0b' : '#7c3aed') : amber ? 'rgba(245,158,11,.07)' : 'background.paper',
                    color: isSelected ? '#fff' : amber ? '#b45309' : 'text.primary',
                    opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : isSelected || saving ? 'default' : 'pointer',
                  }}>
                  {loading ? <CircularProgress size={10} color="inherit" /> : label(pip)}
                </Box>
              </span>
            </Tooltip>
          );
        })}
      </Box>
      {line && <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 1 }}>{line}</Box>}

      {warn && (
        <QuotaWarningDialog
          open
          warnings={warn.warnings}
          onConfirm={() => {
            const c = warn.cadence;
            setWarn(null);
            if (busy.current) return;
            busy.current = true;
            void write(c).finally(() => { busy.current = false; });
          }}
          onCancel={() => setWarn(null)}
        />
      )}
    </Box>
  );
}
